import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  MessageCircle, X, Send, Phone, Bot, Users, Hash, Megaphone, ArrowLeft, UserCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMERGENCY_PHONE = '905-616-7617';
const POLL_MS = 3000;

const genSessionId = () => {
  let id = localStorage.getItem('cb_chat_session');
  if (!id) {
    id = 'cust_' + Math.random().toString(36).slice(2, 12);
    localStorage.setItem('cb_chat_session', id);
  }
  return id;
};

export const ChatWidget = () => {
  const { user, loading: authLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const isEmployee = user?.role === 'employee' || user?.role === 'admin';

  if (authLoading) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open chat"
          data-testid="chat-bubble"
          /* bottom-20 lifts the bubble ~80px so it sits ABOVE the
             fixed Emergent badge (bottom:16px, height:40px) */
          className="fixed bottom-20 right-5 z-50 w-14 h-14 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div
          data-testid="chat-window"
          /* Anchor the chat panel above the Emergent badge too */
          className="fixed bottom-20 right-5 z-50 bg-white rounded-2xl shadow-2xl border border-[#E8E5DF] flex flex-col sm:w-[380px] sm:h-[560px] w-[calc(100vw-24px)] h-[calc(100vh-140px)] max-h-[78vh] overflow-hidden"
        >
          <ChatHeader onClose={() => setOpen(false)} isEmployee={isEmployee} userName={user?.name} />
          {isEmployee ? <EmployeeChat /> : <CustomerChat />}
        </div>
      )}
    </>
  );
};

const ChatHeader = ({ onClose, isEmployee, userName }) => (
  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[#FF3B6B] to-[#E62E58] text-white">
    <div className="flex items-center gap-2">
      {isEmployee ? <Users className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
      <div>
        <p className="font-semibold text-sm" style={{ fontFamily: 'Fredoka' }}>
          {isEmployee ? 'Team Chat' : 'Creative Birthdays Support'}
        </p>
        {isEmployee && userName && <p className="text-[10px] opacity-80">Signed in as {userName}</p>}
      </div>
    </div>
    <div className="flex items-center gap-1">
      <a
        href={`tel:${EMERGENCY_PHONE.replace(/-/g, '')}`}
        className="px-2 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs flex items-center gap-1 font-semibold"
        data-testid="chat-emergency-btn"
        title="Call us"
      >
        <Phone className="w-3 h-3" /> Call
      </a>
      <button
        onClick={onClose}
        className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full"
        data-testid="chat-close-btn"
        aria-label="Close chat"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  </div>
);

/* ---------------- CUSTOMER CHAT ---------------- */
const CustomerChat = () => {
  const [sessionId] = useState(genSessionId());
  const [messages, setMessages] = useState([
    {
      sender_type: 'bot',
      sender_name: 'Bot',
      message: "Hi! I'm the Creative Birthdays Bot. How can I help you? Try: booking status, packages, cancellation policy, or type your question.",
      created_at: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [quickReplies, setQuickReplies] = useState(['Check my booking', 'See packages', 'Cancellation policy', 'Talk to a manager']);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketName, setTicketName] = useState('');
  const [ticketEmail, setTicketEmail] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setMessages(m => [...m, { sender_type: 'customer', sender_name: 'You', message: msg, created_at: new Date().toISOString() }]);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/chat/customer/message`, { session_id: sessionId, message: msg });
      setMessages(m => [...m, { sender_type: 'bot', sender_name: 'Bot', message: res.data.reply, created_at: new Date().toISOString() }]);
      if (res.data.quick_replies) setQuickReplies(res.data.quick_replies);
      if (res.data.offer_ticket) setShowTicketForm(true);
    } catch (e) {
      setMessages(m => [...m, { sender_type: 'bot', sender_name: 'Bot', message: "Sorry, I'm having trouble. Please call " + EMERGENCY_PHONE, created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const submitTicket = async () => {
    if (!ticketEmail || !ticketMessage) {
      toast.error('Email and message required'); return;
    }
    try {
      await axios.post(`${API_URL}/api/chat/customer/ticket`, {
        customer_email: ticketEmail,
        customer_name: ticketName,
        message: ticketMessage,
        session_id: sessionId,
      });
      setMessages(m => [...m, { sender_type: 'bot', sender_name: 'Bot', message: "✅ Ticket created! A manager will reply within 2 hours to " + ticketEmail + ". You can also continue chatting here.", created_at: new Date().toISOString() }]);
      setShowTicketForm(false);
      setTicketMessage('');
    } catch (e) {
      toast.error('Failed to create ticket');
    }
  };

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#FFFDF8]">
        {messages.map((m, i) => <MessageBubble key={i} m={m} isSelf={m.sender_type === 'customer'} />)}
        {sending && <div className="text-xs text-[#6B6B7A] italic">Bot is typing…</div>}
        {showTicketForm && (
          <div className="p-3 bg-white rounded-xl border border-[#E8E5DF] space-y-2" data-testid="ticket-form">
            <p className="text-xs font-semibold">Create Support Ticket</p>
            <input type="text" placeholder="Your name (optional)" value={ticketName} onChange={e => setTicketName(e.target.value)} className="w-full px-3 py-2 border border-[#E8E5DF] rounded-lg text-sm" data-testid="ticket-name" />
            <input type="email" placeholder="Your email *" value={ticketEmail} onChange={e => setTicketEmail(e.target.value)} className="w-full px-3 py-2 border border-[#E8E5DF] rounded-lg text-sm" data-testid="ticket-email" />
            <textarea placeholder="How can we help? *" value={ticketMessage} onChange={e => setTicketMessage(e.target.value)} rows={3} className="w-full px-3 py-2 border border-[#E8E5DF] rounded-lg text-sm" data-testid="ticket-message" />
            <div className="flex gap-2">
              <button onClick={() => setShowTicketForm(false)} className="flex-1 px-3 py-1.5 border border-[#E8E5DF] rounded-lg text-xs">Cancel</button>
              <button onClick={submitTicket} className="flex-1 px-3 py-1.5 bg-[#FF3B6B] text-white rounded-lg text-xs font-semibold" data-testid="submit-ticket">Submit Ticket</button>
            </div>
          </div>
        )}
      </div>
      {quickReplies.length > 0 && !showTicketForm && (
        <div className="px-3 py-2 flex flex-wrap gap-1.5 border-t border-[#E8E5DF] bg-white" data-testid="quick-replies">
          {quickReplies.slice(0, 4).map(q => (
            <button
              key={q}
              onClick={() => send(q)}
              className="px-3 py-1 text-xs rounded-full bg-[#FFF4E6] border border-[#FFD166] hover:bg-[#FFD166]"
              data-testid={`quick-${q.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}
      <div className="p-2 border-t border-[#E8E5DF] bg-white flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Type a message…"
          className="flex-1 px-3 py-2 border border-[#E8E5DF] rounded-full text-sm outline-none focus:border-[#FF3B6B]"
          data-testid="chat-input"
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="w-10 h-10 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center disabled:opacity-50"
          data-testid="chat-send-btn"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </>
  );
};

/* ---------------- EMPLOYEE CHAT ---------------- */
const EmployeeChat = () => {
  const [rooms, setRooms] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [activeMeta, setActiveMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [canPostAnnouncement, setCanPostAnnouncement] = useState(false);
  const [showDmPicker, setShowDmPicker] = useState(false);
  const scrollRef = useRef(null);

  const loadRooms = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chat/employee/rooms`, { withCredentials: true });
      setRooms(res.data.rooms);
      setEmployees(res.data.employees);
      setCanPostAnnouncement(res.data.can_post_announcement);
      setCurrentUserId(res.data.current_user_id);
    } catch (e) {
      // Silent; user might be locked
    }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!activeRoom) return;
    try {
      const res = await axios.get(`${API_URL}/api/chat/employee/messages/${activeRoom}`, { withCredentials: true });
      setMessages(res.data.messages);
    } catch (e) {
      // noop
    }
  }, [activeRoom]);

  useEffect(() => { loadRooms(); }, [loadRooms]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll
  useEffect(() => {
    const interval = setInterval(() => {
      loadRooms();
      loadMessages();
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [loadRooms, loadMessages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const openRoom = (room) => {
    setActiveRoom(room.room_id);
    setActiveMeta(room);
    setShowDmPicker(false);
  };

  const openDmWith = async (otherId, otherName) => {
    try {
      const res = await axios.post(`${API_URL}/api/chat/employee/dm/start`, { other_user_id: otherId }, { withCredentials: true });
      setActiveRoom(res.data.room_id);
      setActiveMeta({ room_id: res.data.room_id, type: 'dm', name: otherName, can_post: true });
      setShowDmPicker(false);
    } catch (e) {
      toast.error('Failed to open DM');
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !activeRoom) return;
    try {
      const res = await axios.post(`${API_URL}/api/chat/employee/message`,
        { room_id: activeRoom, message: text },
        { withCredentials: true });
      setMessages(m => [...m, res.data]);
      setInput('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    }
  };

  if (!activeRoom && !showDmPicker) {
    // Room list
    return (
      <>
        <div className="flex-1 overflow-y-auto bg-[#FFFDF8]">
          {rooms.map(r => (
            <button
              key={r.room_id}
              onClick={() => openRoom(r)}
              className="w-full px-3 py-3 border-b border-[#E8E5DF] flex items-center gap-3 hover:bg-white text-left"
              data-testid={`room-${r.room_id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#FF3B6B]/10 flex items-center justify-center flex-shrink-0">
                {r.type === 'channel'
                  ? (r.channel === 'announcements' ? <Megaphone className="w-4 h-4 text-[#FF3B6B]" /> : <Hash className="w-4 h-4 text-[#FF3B6B]" />)
                  : <UserCircle className="w-5 h-5 text-[#FF3B6B]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{r.name}</p>
                <p className="text-xs text-[#6B6B7A] truncate">{r.last_message?.message || 'No messages yet'}</p>
              </div>
              {r.unread > 0 && (
                <span className="ml-auto px-1.5 py-0.5 bg-[#FF3B6B] text-white rounded-full text-xs font-bold" data-testid={`unread-${r.room_id}`}>{r.unread}</span>
              )}
            </button>
          ))}
        </div>
        <div className="p-2 border-t border-[#E8E5DF] bg-white">
          <button
            onClick={() => setShowDmPicker(true)}
            className="w-full px-4 py-2 rounded-full bg-[#FF3B6B] text-white text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="start-dm-btn"
          >
            <Users className="w-4 h-4" /> Start Direct Message
          </button>
        </div>
      </>
    );
  }

  if (showDmPicker) {
    return (
      <>
        <div className="flex items-center gap-2 p-2 border-b border-[#E8E5DF] bg-white">
          <button onClick={() => setShowDmPicker(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#FFFDF8]"><ArrowLeft className="w-4 h-4" /></button>
          <p className="font-semibold text-sm">Pick a teammate</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {employees.length === 0 && <p className="p-6 text-center text-xs text-[#6B6B7A]">No other teammates yet</p>}
          {employees.map(e => (
            <button
              key={e.user_id}
              onClick={() => openDmWith(e.user_id, e.name)}
              className="w-full px-3 py-3 border-b border-[#E8E5DF] flex items-center gap-3 hover:bg-[#FFFDF8] text-left"
              data-testid={`dm-pick-${e.user_id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#06D6A0]/10 flex items-center justify-center">
                <span className="text-xs font-bold text-[#06D6A0]">{e.name.slice(0, 2).toUpperCase()}</span>
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{e.name}</p>
                <p className="text-xs text-[#6B6B7A] capitalize">{(e.employee_role || '').replace('_', ' ')}</p>
              </div>
            </button>
          ))}
        </div>
      </>
    );
  }

  // Active room view
  return (
    <>
      <div className="flex items-center gap-2 p-2 border-b border-[#E8E5DF] bg-white">
        <button onClick={() => { setActiveRoom(null); setActiveMeta(null); loadRooms(); }} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#FFFDF8]" data-testid="back-to-rooms">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <p className="font-semibold text-sm flex-1">{activeMeta?.name}</p>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#FFFDF8]">
        {messages.length === 0 ? (
          <p className="text-xs text-center text-[#6B6B7A] py-8">No messages yet. Start the conversation!</p>
        ) : messages.map((m, i) => (
          <MessageBubble key={m.message_id || i} m={m} isSelf={m.sender_id === currentUserId} />
        ))}
      </div>
      {activeMeta?.can_post === false ? (
        <div className="p-3 border-t border-[#E8E5DF] bg-[#FFF4E6] text-xs text-center text-[#6B6B7A]">
          Only managers/admins can post in #announcements
        </div>
      ) : (
        <div className="p-2 border-t border-[#E8E5DF] bg-white flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="Type a message…"
            className="flex-1 px-3 py-2 border border-[#E8E5DF] rounded-full text-sm outline-none focus:border-[#FF3B6B]"
            data-testid="emp-chat-input"
          />
          <button
            onClick={send}
            disabled={!input.trim()}
            className="w-10 h-10 rounded-full bg-[#FF3B6B] text-white flex items-center justify-center disabled:opacity-50"
            data-testid="emp-chat-send"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  );
};

const MessageBubble = ({ m, isSelf }) => {
  const isBot = m.sender_type === 'bot';
  const isAdmin = m.sender_type === 'admin';
  const bg = isSelf ? 'bg-[#FF3B6B] text-white' : isBot ? 'bg-[#FFF4E6] border border-[#FFD166]' : isAdmin ? 'bg-[#6366F1]/10 border border-[#6366F1]/30' : 'bg-white border border-[#E8E5DF]';
  return (
    <div className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${bg} ${isSelf ? 'rounded-br-sm' : 'rounded-bl-sm'}`}>
        {!isSelf && (
          <p className="text-[10px] font-semibold opacity-70 mb-0.5">
            {m.sender_name}
          </p>
        )}
        <p className="whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>{m.message}</p>
        <p className={`text-[9px] mt-0.5 ${isSelf ? 'opacity-70' : 'opacity-50'}`}>
          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
        </p>
      </div>
    </div>
  );
};

export default ChatWidget;
